import { escapeHtml } from "./pdfUtils";

const ACCENT = "#5ef0d8";
const ACCENT_DIM = "#2d8b7e";
const LAVENDER = "#7fb4ff";

function polarToXY(cx, cy, radius, degree) {
  const rad = ((degree - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(rad),
    y: cy + radius * Math.sin(rad),
  };
}

function clampHueColor(value, fallback = "#9f8cff") {
  const text = String(value || "").trim();
  if (!text) return fallback;
  return text;
}

function buildWheelSvg(wheelData, labels) {
  if (!wheelData || !Array.isArray(wheelData.signs) || !wheelData.signs.length || !Array.isArray(wheelData.planets)) {
    return `<div class="empty-wheel">${escapeHtml(labels.wheelFallback || "")}</div>`;
  }

  const cx = 220;
  const cy = 220;
  const outerRadius = 190;
  const innerRadius = 136;
  const planetRadius = 112;
  const aspectRadius = 86;
  const signArc = 360 / 12;

  const signSlices = wheelData.signs.slice(0, 12).map((sign, idx) => {
    const start = idx * signArc;
    const end = (idx + 1) * signArc;
    const p1 = polarToXY(cx, cy, innerRadius, start);
    const p2 = polarToXY(cx, cy, outerRadius, start);
    const p3 = polarToXY(cx, cy, outerRadius, end);
    const p4 = polarToXY(cx, cy, innerRadius, end);
    const labelPos = polarToXY(cx, cy, (innerRadius + outerRadius) / 2 + 4, start + signArc / 2);
    const isSunSign = Number(wheelData.sunIndex) === idx;
    return `
      <g>
        <path d="M ${p1.x},${p1.y} L ${p2.x},${p2.y} A ${outerRadius},${outerRadius} 0 0 1 ${p3.x},${p3.y} L ${p4.x},${p4.y} A ${innerRadius},${innerRadius} 0 0 0 ${p1.x},${p1.y}"
          fill="${isSunSign ? "#173840" : "#0f1326"}"
          stroke="${isSunSign ? "#5ef0d8" : "#2a2f45"}"
          stroke-width="0.8" />
        <text x="${labelPos.x}" y="${labelPos.y}" text-anchor="middle" dominant-baseline="middle" fill="${escapeHtml(clampHueColor(sign.color, LAVENDER))}" font-size="14">${escapeHtml(sign.symbol || "")}</text>
      </g>
    `;
  }).join("");

  const aspectLines = (wheelData.aspects || []).map((aspect, idx) => {
    const p1Deg = Number(wheelData.planets?.[Number(aspect.p1)]?.degree || 0);
    const p2Deg = Number(wheelData.planets?.[Number(aspect.p2)]?.degree || 0);
    const p1 = polarToXY(cx, cy, aspectRadius, p1Deg);
    const p2 = polarToXY(cx, cy, aspectRadius, p2Deg);
    const dash = Number(aspect.deg) === 90 ? "4,3" : Number(aspect.deg) === 180 ? "6,3" : "none";
    return `<line x1="${p1.x}" y1="${p1.y}" x2="${p2.x}" y2="${p2.y}" stroke="${escapeHtml(clampHueColor(aspect.color, LAVENDER))}" stroke-width="1.2" stroke-dasharray="${dash}" />`;
  }).join("");

  const planetNodes = (wheelData.planets || []).map((planet) => {
    const degree = Number(planet.degree || 0);
    const node = polarToXY(cx, cy, planetRadius, degree);
    const dotColor = clampHueColor(planet.color, ACCENT);
    return `
      <g>
        <circle cx="${node.x}" cy="${node.y}" r="12" fill="#12162a" stroke="${escapeHtml(dotColor)}" stroke-width="1.2" />
        <text x="${node.x}" y="${node.y}" text-anchor="middle" dominant-baseline="middle" fill="${escapeHtml(dotColor)}" font-size="12">${escapeHtml(planet.symbol || "")}</text>
      </g>
    `;
  }).join("");

  const risingDeg = Number(wheelData.risingIndex || 0) * 30;
  const risingStart = polarToXY(cx, cy, aspectRadius - 2, risingDeg);
  const risingEnd = polarToXY(cx, cy, outerRadius + 1, risingDeg);

  const wheelLabel = escapeHtml(labels.wheel || "");
  const svgMarkup = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 440 440" class="wheel-svg" role="img" aria-label="${wheelLabel}">
      ${signSlices}
      <circle cx="${cx}" cy="${cy}" r="${outerRadius}" fill="none" stroke="#2f3552" stroke-width="1" />
      <circle cx="${cx}" cy="${cy}" r="${innerRadius}" fill="none" stroke="#252b44" stroke-width="0.8" />
      ${aspectLines}
      <circle cx="${cx}" cy="${cy}" r="${aspectRadius}" fill="rgba(7,10,24,0.65)" stroke="#252b44" stroke-width="0.8" />
      <line x1="${risingStart.x}" y1="${risingStart.y}" x2="${risingEnd.x}" y2="${risingEnd.y}" stroke="${LAVENDER}" stroke-width="1.6" />
      ${planetNodes}
      <text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="middle" font-size="20" fill="${ACCENT}">✦</text>
    </svg>
  `.trim();
  const wheelDataUri = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`;
  return `<div class="wheel-svg-image" role="img" aria-label="${wheelLabel}" data-wheel-src="${escapeHtml(wheelDataUri)}" style="background-image:url('${wheelDataUri}');"></div>`;
}

function energyBars(score) {
  const clamped = Math.max(0, Math.min(100, Number(score) || 0));
  const total = 10;
  const filled = Math.floor(clamped / 10);
  const half = clamped % 10 >= 5 ? 1 : 0;
  let html = "";
  for (let i = 0; i < total; i++) {
    if (i < filled) html += `<div class="energy-bar filled"></div>`;
    else if (i === filled && half) html += `<div class="energy-bar half"></div>`;
    else html += `<div class="energy-bar"></div>`;
  }
  return html;
}

function truncate(text, max) {
  const value = String(text || "").trim();
  if (!value) return "";
  if (value.length <= max) return value;
  return `${value.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}

function opt(content) {
  return content || "";
}

function hasRenderableValue(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === "number") return Number.isFinite(value);
  return String(value).trim().length > 0;
}

export function buildWesternAstrologyPdfHtml({
  dob,
  birthTime,
  birthPlace,
  exportedAt,
  sun,
  moon,
  rising,
  bigThreeInsights = [],
  confidenceNotes = [],
  aspectInsights = [],
  orbExplainer = "",
  housesDetails = [],
  synthesisText,
  planetRows,
  elementBalance,
  wheelData,
  sign,
  daily,
  labels = {},
}) {
  const l = labels;

  const signName = escapeHtml(sign?.name || sun?.n || "");
  const signGlyph = escapeHtml(sign?.glyph || sun?.s || "");
  const signDates = escapeHtml(sign?.dates || "");
  const signElement = escapeHtml(sign?.element || sun?.el || "");
  const signModality = escapeHtml(sign?.modality || sun?.mod || "");
  const signRuler = escapeHtml(sign?.ruler || "");
  const dailyLuckyNumber = daily?.lucky_number || daily?.luckyNumber || "";
  const dailyPowerColor = daily?.power_color || daily?.powerColor || "";
  const dailyPeakHour = daily?.peak_hour || daily?.peakHour || "";
  const dailyPeakHourNote = daily?.peak_hour_note || daily?.peakHourNote || "";
  const displayLuckyNumber = hasRenderableValue(dailyLuckyNumber) ? String(dailyLuckyNumber) : "—";
  const displayPowerColor = hasRenderableValue(dailyPowerColor) ? String(dailyPowerColor) : "—";
  const displayPeakHour = hasRenderableValue(dailyPeakHour) ? String(dailyPeakHour) : "—";
  const displayPeakHourNote = hasRenderableValue(dailyPeakHourNote) ? String(dailyPeakHourNote) : "";
  const dailyEnergyValue = Number(
    daily?.energy ?? daily?.energy_index ?? daily?.energyIndex ?? daily?.overall_energy ?? 0
  );

  const hasEnergy = Number.isFinite(dailyEnergyValue) && dailyEnergyValue > 0;
  const energyBlock = hasEnergy ? `
    <div class="energy-block">
      <div class="energy-label">${escapeHtml(l.energyIndex)}</div>
      <div class="energy-score">${escapeHtml(Math.round(dailyEnergyValue))}<sup>%</sup></div>
      <div class="energy-bars">${energyBars(dailyEnergyValue)}</div>
    </div>` : "";

  const tags = [signElement, signModality, signRuler ? `♃ ${signRuler}` : ""]
    .filter(Boolean)
    .map((tag) => `<span class="tag">${escapeHtml(tag)}</span>`)
    .join("");

  const heroBlock = signName ? `
    <div class="sign-hero page-break-avoid">
      <div class="sign-symbol-big">${signGlyph}</div>
      <div class="sign-info">
        <h1>${signName}</h1>
        ${signDates ? `<div class="dates">${signDates}</div>` : ""}
        ${tags ? `<div class="element-ruler">${tags}</div>` : ""}
      </div>
      ${energyBlock}
      <div class="orbit-deco"></div>
    </div>` : "";

  const miniStats = `
    <div class="mini-stats page-break-avoid stats-3">
      <div class="stat-item">
        <div class="stat-label">${escapeHtml(l.luckyNumber)}</div>
        <div class="stat-value stat-value-lg">${escapeHtml(displayLuckyNumber)}</div>
      </div>
      <div class="stat-item">
        <div class="stat-label">${escapeHtml(l.powerColor)}</div>
        <div class="stat-value stat-value-md">${escapeHtml(displayPowerColor)}</div>
      </div>
      <div class="stat-item">
        <div class="stat-label">${escapeHtml(l.peakHour)}</div>
        <div class="stat-value stat-value-md">${escapeHtml(displayPeakHour)}</div>
        ${displayPeakHourNote ? `<div class="stat-sub">${escapeHtml(displayPeakHourNote)}</div>` : ""}
      </div>
    </div>`;

  const dailyText = daily?.daily_reading;
  const dailyThread = daily?.today_thread;
  const dailyCard = dailyText ? `
    <div class="card daily">
      <div class="card-label">${escapeHtml(l.dailySection)}</div>
      ${dailyThread ? `<p><strong>${escapeHtml(l.todayThread || "")}</strong> ${escapeHtml(dailyThread)}</p>` : ""}
      <p>${escapeHtml(dailyText)}</p>
    </div>` : "";

  const triforceCards = [
    daily?.love_relationships ? `<div class="card"><div class="card-label">${escapeHtml(l.loveLabel)}</div><p>${escapeHtml(daily.love_relationships)}</p></div>` : "",
    daily?.career_purpose ? `<div class="card"><div class="card-label">${escapeHtml(l.careerLabel)}</div><p>${escapeHtml(daily.career_purpose)}</p></div>` : "",
    daily?.wellness_body ? `<div class="card"><div class="card-label">${escapeHtml(l.wellnessLabel)}</div><p>${escapeHtml(daily.wellness_body)}</p></div>` : "",
  ].filter(Boolean);
  const triforceBlock = triforceCards.length ? `<div class="triforce">${triforceCards.join("")}</div>` : "";

  const bigThreeStrip = (bigThreeInsights && bigThreeInsights.length) ? `
    <div class="big-three">
      <div class="card big-three-card"><div class="card-label">${escapeHtml(l.sun)}</div><div class="value">${escapeHtml(sun?.n || "")}</div><div class="sub">${escapeHtml(sun?.el || "")} · ${escapeHtml(sun?.mod || "")}</div>${bigThreeInsights[0] ? `<p class="hint">${escapeHtml(bigThreeInsights[0])}</p>` : ""}</div>
      <div class="card big-three-card"><div class="card-label">${escapeHtml(l.moon)}</div><div class="value">${escapeHtml(moon?.n || "")}</div><div class="sub">${escapeHtml(moon?.el || "")} · ${escapeHtml(moon?.mod || "")}</div>${bigThreeInsights[1] ? `<p class="hint">${escapeHtml(bigThreeInsights[1])}</p>` : ""}</div>
      <div class="card big-three-card"><div class="card-label">${escapeHtml(l.rising)}</div><div class="value">${escapeHtml(rising?.n || "")}</div><div class="sub">${escapeHtml(rising?.el || "")} · ${escapeHtml(rising?.mod || "")}</div>${bigThreeInsights[2] ? `<p class="hint">${escapeHtml(bigThreeInsights[2])}</p>` : ""}</div>
    </div>` : "";

  const wheelSection = wheelData ? `
    <div class="card full page-break-avoid">
      <div class="card-label">${escapeHtml(l.wheel)}</div>
      <div class="wheel-shell">${buildWheelSvg(wheelData, l)}</div>
    </div>` : "";

  const synthesisCard = synthesisText ? `
    <div class="card full synthesis-card">
      <div class="card-label">${escapeHtml(l.synthesis)}</div>
      <p>${escapeHtml(synthesisText)}</p>
    </div>` : "";

  const planetGridItems = (planetRows || []).map((row) => {
    const description = truncate(row.description, 140);
    return `
      <div class="planet-card">
        <div class="planet-card-head">
          <span class="planet-symbol">${escapeHtml(row.symbol || "●")}</span>
          <span class="planet-name-text">${escapeHtml(row.planet || "")}</span>
        </div>
        <div class="planet-card-body">
          <span>${escapeHtml(row.sign || "")} ${escapeHtml(row.signSymbol || "")}</span>
          <span class="planet-degree">${escapeHtml(Number(row.degree || 0).toFixed(2))}°</span>
        </div>
        <div class="planet-card-meta">${escapeHtml(row.element || "")} · ${escapeHtml(row.motion || "")}</div>
        ${description ? `<div class="planet-card-desc">${escapeHtml(description)}</div>` : ""}
      </div>`;
  }).join("");
  const planetSection = planetRows && planetRows.length ? `
    <div class="card full">
      <div class="card-label">${escapeHtml(l.planetaryPositions)}</div>
      <div class="planet-grid">${planetGridItems}</div>
    </div>` : "";

  const aspectItems = (aspectInsights || []).map((item) => `
    <div class="aspect-row">
      <div class="aspect-main">${escapeHtml(item.planetA || "")} · ${escapeHtml(item.aspectLabel || "")} · ${escapeHtml(item.planetB || "")}${item.orbLabel ? ` <span class="aspect-orb">(${escapeHtml(item.orbLabel)})</span>` : ""}</div>
      ${item.summary ? `<div class="aspect-tip">${escapeHtml(truncate(item.summary, 200))}</div>` : ""}
      ${item.practical_tip ? `<div class="aspect-tip mono"><span class="tip-key">${escapeHtml(l.practicalTip)}</span> · ${escapeHtml(truncate(item.practical_tip, 160))}</div>` : ""}
    </div>`).join("");
  const aspectSection = aspectInsights && aspectInsights.length ? `
    <div class="card full">
      <div class="card-label">${escapeHtml(l.aspects)}</div>
      <div class="aspect-list">${aspectItems}</div>
      ${orbExplainer ? `<div class="hint">${escapeHtml(orbExplainer)}</div>` : ""}
    </div>` : "";

  const houseItems = (housesDetails || []).map((item) => {
    const planetsLine = item.planets && item.planets.length
      ? item.planets.map((planet) => `${escapeHtml(planet.symbol || "")} ${escapeHtml(planet.name || "")} ${escapeHtml(planet.degree || "")}°${planet.retrograde ? " <span class=\"rx\">Rx</span>" : ""}`).join(" · ")
      : `<span class="dim">${escapeHtml(l.noPlanets)}</span>`;
    const insightLine = [item.themeInsight, item.expressionInsight, item.growthInsight].filter(Boolean).join(" · ");
    return `
      <div class="house-row page-break-avoid">
        <div class="house-main"><span class="house-num">${escapeHtml(item.house)}</span> · ${escapeHtml(item.theme || "")} · ${escapeHtml(item.sign || "")} ${escapeHtml(item.signSymbol || "")} · <span class="house-cusp">${escapeHtml(item.cusp || "")}°</span></div>
        <div class="house-sub">${escapeHtml(item.element || "")} · ${escapeHtml(item.modality || "")} · <span class="dim">${escapeHtml(l.planetsInHouse)}:</span> ${planetsLine}</div>
        ${insightLine ? `<div class="house-sub mono">${escapeHtml(truncate(insightLine, 240))}</div>` : ""}
      </div>`;
  }).join("");
  const housesSection = housesDetails && housesDetails.length ? `
    <div class="card full">
      <div class="card-label">${escapeHtml(l.housesHighlights)}</div>
      <div class="house-list">${houseItems}</div>
    </div>` : "";

  const balanceItems = (elementBalance || []).map((item) => `
    <div class="balance-item">
      <div class="balance-label">${escapeHtml(item.el)}</div>
      <div class="balance-value">${escapeHtml(item.count)} <span class="balance-out">/ 9</span></div>
    </div>`).join("");
  const balanceSection = elementBalance && elementBalance.length ? `
    <div class="card full">
      <div class="card-label">${escapeHtml(l.elementalBalance)}</div>
      <div class="balance">${balanceItems}</div>
    </div>` : "";

  const harmonicItems = (daily?.harmonic_signs || []).map((item) => `
    <div class="compat-sign">
      <span class="sym">${escapeHtml(item.glyph || "")}</span>
      <span class="nm">${escapeHtml(item.name || "")}</span>
    </div>`).join("");
  const harmonicSection = daily?.harmonic_signs?.length ? `
    <div class="card">
      <div class="card-label">${escapeHtml(l.harmonicLabel)}</div>
      <div class="compat-signs">${harmonicItems}</div>
    </div>` : "";

  const planetaryInfluenceItems = (daily?.planetary_influences || []).map((item) => `
    <li>
      <span class="planet-name">${escapeHtml(item.name || "")}</span>
      <span class="planet-influence">${escapeHtml(item.influence || "")}</span>
    </li>`).join("");
  const planetaryInfluencesSection = daily?.planetary_influences?.length ? `
    <div class="card">
      <div class="card-label">${escapeHtml(l.planetaryInfluencesLabel)}</div>
      <ul class="planet-list-daily">${planetaryInfluenceItems}</ul>
    </div>` : "";

  const dailySidesRow = (harmonicSection || planetaryInfluencesSection) ? `
    <div class="two-col">${planetaryInfluencesSection}${harmonicSection}</div>` : "";

  const affirmationCard = daily?.affirmation ? `
    <div class="card full affirmation">
      <div class="card-label centered">${escapeHtml(l.affirmationLabel)}</div>
      <p>"${escapeHtml(daily.affirmation)}"</p>
      ${signName ? `<div class="attr">— ${signName}</div>` : ""}
    </div>` : "";

  const confidenceCard = confidenceNotes && confidenceNotes.length ? `
    <div class="card full">
      <div class="card-label">${escapeHtml(l.confidenceNotes)}</div>
      ${confidenceNotes.map((note) => `<div class="hint">${escapeHtml(note)}</div>`).join("")}
    </div>` : "";

  const dailyDivider = (miniStats || dailyCard || triforceBlock || dailySidesRow || affirmationCard) ? `
    <div class="section-divider">${escapeHtml(l.dailySection)}</div>` : "";
  const natalDivider = (bigThreeStrip || wheelSection || synthesisCard || planetSection || aspectSection || housesSection || balanceSection) ? `
    <div class="section-divider">${escapeHtml(l.natalSection)}</div>` : "";
  // TODO: Re-enable natal and below sections by setting this to true.
  const INCLUDE_NATAL_AND_BELOW_SECTIONS = false;
  const natalAndBelowBlock = INCLUDE_NATAL_AND_BELOW_SECTIONS ? `
        ${opt(natalDivider)}
        ${opt(bigThreeStrip)}
        ${opt(wheelSection)}
        ${opt(synthesisCard)}
        ${opt(planetSection)}
        ${opt(aspectSection)}
        ${opt(housesSection)}
        ${opt(balanceSection)}
        ${opt(confidenceCard)}` : `
        <!-- TODO: Natal and below sections are temporarily disabled. -->
      `;

  const meta = [
    `${escapeHtml(l.dob)}: ${escapeHtml(dob)}`,
    birthTime ? escapeHtml(birthTime) : "",
    birthPlace ? escapeHtml(birthPlace) : "",
  ].filter(Boolean).join(" · ");

  return `
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,700;1,400;1,500&family=DM+Sans:wght@300;400;500&family=DM+Mono:wght@300;400&display=swap" rel="stylesheet">
    <style>
      @page { size: A4; margin: 12mm; }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        font-family: 'DM Sans', 'Inter', 'Segoe UI', Arial, sans-serif;
        font-weight: 300;
        color: #e8ebff;
        background: #050816;
      }
      .sheet {
        position: relative;
        overflow: hidden;
        border-radius: 18px;
        background:
          radial-gradient(ellipse 60% 40% at 20% 100%, rgba(127,180,255,0.10) 0%, transparent 60%),
          radial-gradient(circle at top left, #172148, #080b1f 56%, #050816);
        border: 1px solid rgba(94,240,216,0.20);
      }
      .sheet::before {
        content: '';
        position: absolute;
        inset: 0;
        background-image:
          radial-gradient(1px 1px at 10% 15%, rgba(255,255,255,0.45) 0%, transparent 100%),
          radial-gradient(1px 1px at 25% 60%, rgba(255,255,255,0.30) 0%, transparent 100%),
          radial-gradient(1.2px 1.2px at 40% 30%, rgba(255,255,255,0.40) 0%, transparent 100%),
          radial-gradient(1px 1px at 55% 80%, rgba(255,255,255,0.25) 0%, transparent 100%),
          radial-gradient(1px 1px at 70% 20%, rgba(255,255,255,0.40) 0%, transparent 100%),
          radial-gradient(1.2px 1.2px at 80% 55%, rgba(255,255,255,0.30) 0%, transparent 100%),
          radial-gradient(1px 1px at 90% 10%, rgba(255,255,255,0.45) 0%, transparent 100%),
          radial-gradient(1px 1px at 15% 85%, rgba(255,255,255,0.22) 0%, transparent 100%),
          radial-gradient(1px 1px at 60% 45%, rgba(255,255,255,0.32) 0%, transparent 100%),
          radial-gradient(1px 1px at 35% 95%, rgba(255,255,255,0.20) 0%, transparent 100%),
          radial-gradient(1.2px 1.2px at 5% 50%, rgba(255,255,255,0.30) 0%, transparent 100%),
          radial-gradient(1px 1px at 95% 70%, rgba(255,255,255,0.40) 0%, transparent 100%);
        pointer-events: none;
        z-index: 0;
      }
      .sheet > * { position: relative; z-index: 1; }

      .header {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        padding: 26px 28px 18px;
        border-bottom: 1px solid rgba(94,240,216,0.20);
      }
      .brand {
        font-family: 'Playfair Display', 'Georgia', serif;
        font-size: 22px;
        letter-spacing: 0.12em;
        color: ${ACCENT};
        text-transform: uppercase;
      }
      .brand span {
        display: block;
        font-family: 'DM Sans', sans-serif;
        font-size: 10px;
        font-weight: 400;
        letter-spacing: 0.25em;
        color: #8a8ea8;
        text-transform: uppercase;
        margin-top: 4px;
      }
      .header-date {
        text-align: right;
        font-family: 'DM Mono', monospace;
        font-size: 10.5px;
        color: #8a8ea8;
        letter-spacing: 0.08em;
        line-height: 1.7;
      }
      .header-date strong {
        display: block;
        color: ${LAVENDER};
        font-size: 12px;
        letter-spacing: 0.15em;
        margin-bottom: 2px;
      }

      .content {
        padding: 22px 24px 24px;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 14px;
      }
      .full { grid-column: 1 / -1; }
      .two-col { grid-column: 1 / -1; display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

      .section-divider {
        grid-column: 1 / -1;
        font-size: 10px;
        letter-spacing: 0.3em;
        text-transform: uppercase;
        color: ${ACCENT};
        margin: 6px 0 -2px;
        padding-bottom: 6px;
        border-bottom: 1px solid rgba(94,240,216,0.15);
        font-family: 'DM Mono', monospace;
      }

      /* Sign Hero */
      .sign-hero {
        grid-column: 1 / -1;
        position: relative;
        overflow: hidden;
        background: rgba(19,17,42,0.85);
        border: 1px solid rgba(94,240,216,0.20);
        border-top: 2px solid ${ACCENT};
        padding: 26px 30px;
        display: grid;
        grid-template-columns: auto 1fr auto;
        align-items: center;
        gap: 24px;
        border-radius: 12px;
      }
      .sign-symbol-big {
        display: block;
        font-size: 64px;
        line-height: 1.04;
        padding: 2px 0;
        color: ${ACCENT};
        filter: drop-shadow(0 0 18px rgba(94,240,216,0.45));
      }
      .sign-info h1 {
        margin: 0;
        font-family: 'Playfair Display', 'Georgia', serif;
        font-size: 34px;
        font-weight: 500;
        letter-spacing: 0.02em;
        line-height: 1.1;
        color: #ffffff;
      }
      .sign-info .dates {
        font-size: 11px;
        letter-spacing: 0.2em;
        color: #8a8ea8;
        text-transform: uppercase;
        margin-top: 6px;
      }
      .sign-info .element-ruler {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        margin-top: 10px;
      }
      .tag {
        font-size: 9.5px;
        letter-spacing: 0.2em;
        text-transform: uppercase;
        padding: 4px 10px;
        border: 1px solid rgba(94,240,216,0.45);
        color: ${LAVENDER};
        border-radius: 3px;
      }
      .energy-block { text-align: center; }
      .energy-label {
        font-size: 9px;
        letter-spacing: 0.25em;
        text-transform: uppercase;
        color: #8a8ea8;
        margin-bottom: 6px;
      }
      .energy-score {
        font-family: 'Playfair Display', 'Georgia', serif;
        font-size: 44px;
        font-weight: 400;
        line-height: 1;
        color: ${ACCENT};
        text-shadow: 0 0 24px rgba(94,240,216,0.5);
      }
      .energy-score sup { font-size: 18px; vertical-align: super; }
      .energy-bars {
        display: flex;
        gap: 3px;
        margin-top: 8px;
        justify-content: center;
      }
      .energy-bar {
        width: 5px;
        height: 18px;
        background: #13112a;
        border-radius: 2px;
      }
      .energy-bar.filled { background: ${ACCENT}; }
      .energy-bar.half { background: ${ACCENT_DIM}; }
      .orbit-deco {
        position: absolute;
        right: -36px;
        top: 50%;
        transform: translateY(-50%);
        width: 150px;
        height: 150px;
        border-radius: 50%;
        border: 1px solid rgba(94,240,216,0.14);
        pointer-events: none;
        opacity: 0.6;
      }
      .orbit-deco::before {
        content: '';
        position: absolute;
        inset: 16px;
        border-radius: 50%;
        border: 1px solid rgba(127,180,255,0.10);
      }

      /* Mini stats */
      .mini-stats {
        grid-column: 1 / -1;
        display: grid;
        grid-template-columns: repeat(var(--cols, 3), 1fr);
        border: 1px solid rgba(94,240,216,0.20);
        border-radius: 10px;
        overflow: hidden;
      }
      .mini-stats.stats-1 { --cols: 1; }
      .mini-stats.stats-2 { --cols: 2; }
      .mini-stats.stats-3 { --cols: 3; }
      .stat-item {
        padding: 16px 20px;
        border-right: 1px solid rgba(94,240,216,0.12);
        text-align: center;
      }
      .stat-item:last-child { border-right: none; }
      .stat-label {
        font-size: 9px;
        letter-spacing: 0.3em;
        text-transform: uppercase;
        color: #8a8ea8;
        margin-bottom: 6px;
      }
      .stat-value {
        font-family: 'Playfair Display', 'Georgia', serif;
        color: #ffffff;
      }
      .stat-value-lg { font-size: 28px; color: ${ACCENT}; }
      .stat-value-md { font-size: 15px; letter-spacing: 0.06em; }
      .stat-sub {
        font-size: 10px;
        color: #8a8ea8;
        margin-top: 4px;
        letter-spacing: 0.06em;
      }

      /* Cards */
      .card {
        position: relative;
        overflow: hidden;
        background: rgba(19,17,42,0.85);
        border: 1px solid rgba(94,240,216,0.20);
        padding: 18px 20px;
        border-radius: 10px;
      }
      .card::before {
        content: '';
        position: absolute;
        top: 0; left: 0;
        width: 2px; height: 100%;
        background: linear-gradient(to bottom, ${ACCENT}, transparent);
        opacity: 0.6;
      }
      .card-label {
        font-size: 9px;
        letter-spacing: 0.32em;
        text-transform: uppercase;
        color: ${ACCENT};
        margin-bottom: 10px;
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: 500;
      }
      .card-label::after {
        content: '';
        flex: 1;
        height: 1px;
        background: rgba(94,240,216,0.20);
      }
      .card-label.centered { justify-content: center; }
      .card-label.centered::after { display: none; }
      .card p {
        margin: 0;
        font-size: 12px;
        line-height: 1.7;
        color: #e8ebff;
      }
      .card.daily p { font-size: 12.5px; line-height: 1.75; }

      /* Triforce */
      .triforce {
        grid-column: 1 / -1;
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 12px;
      }
      .triforce .card { padding: 14px 16px; }
      .triforce .card .card-label { margin-bottom: 8px; }
      .triforce .card p { font-size: 11.5px; line-height: 1.65; }

      /* Big three strip */
      .big-three {
        grid-column: 1 / -1;
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 12px;
      }
      .big-three-card { padding: 14px 16px; }
      .big-three-card .value {
        font-family: 'Playfair Display', 'Georgia', serif;
        font-size: 18px;
        color: #ffffff;
        margin-bottom: 2px;
      }
      .big-three-card .sub {
        font-size: 10.5px;
        color: ${LAVENDER};
        letter-spacing: 0.05em;
      }
      .big-three-card .hint {
        margin-top: 8px;
        font-size: 11px;
        color: #c9d0f4;
        line-height: 1.55;
      }

      /* Affirmation */
      .card.affirmation {
        text-align: center;
        background: linear-gradient(135deg, rgba(94,240,216,0.10), rgba(127,180,255,0.07));
        border-color: rgba(94,240,216,0.50);
        padding: 28px 40px;
      }
      .card.affirmation p {
        font-family: 'Playfair Display', 'Georgia', serif;
        font-size: 17px;
        font-style: italic;
        line-height: 1.55;
        color: ${ACCENT};
        max-width: 580px;
        margin: 0 auto;
      }
      .card.affirmation .attr {
        font-family: 'DM Mono', monospace;
        font-size: 9.5px;
        letter-spacing: 0.25em;
        color: #8a8ea8;
        margin-top: 12px;
        text-transform: uppercase;
      }

      /* Synthesis */
      .synthesis-card {
        background: linear-gradient(180deg, rgba(94,240,216,0.10), rgba(94,240,216,0.04));
        border-color: rgba(94,240,216,0.40);
      }
      .synthesis-card p { color: #d8f5ee; font-size: 12px; line-height: 1.65; }

      /* Wheel */
      .wheel-shell {
        text-align: center;
        padding: 4px;
        -webkit-user-select: none;
        user-select: none;
        pointer-events: none;
        -webkit-touch-callout: none;
      }
      .wheel-svg { width: 100%; max-width: 380px; height: auto; display: inline-block; }
      .wheel-svg-image {
        width: 100%;
        max-width: 380px;
        aspect-ratio: 1 / 1;
        margin: 0 auto;
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
      }
      .wheel-svg,
      .wheel-svg * {
        -webkit-user-select: none;
        user-select: none;
        pointer-events: none;
      }
      .empty-wheel { padding: 14px; color: #c6cff4; font-size: 12px; }

      /* Planet grid */
      .planet-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 8px;
      }
      .planet-card {
        border: 1px solid rgba(94,240,216,0.14);
        border-radius: 8px;
        padding: 10px 12px;
        background: rgba(255,255,255,0.02);
      }
      .planet-card-head {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 4px;
      }
      .planet-symbol {
        font-size: 16px;
        color: ${ACCENT};
      }
      .planet-name-text {
        font-size: 11px;
        letter-spacing: 0.12em;
        color: #c9d0f4;
        text-transform: uppercase;
      }
      .planet-card-body {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        font-size: 12.5px;
        color: ${LAVENDER};
        font-family: 'Playfair Display', 'Georgia', serif;
      }
      .planet-degree {
        font-family: 'DM Mono', monospace;
        font-size: 11px;
        color: #c9d0f4;
      }
      .planet-card-meta {
        margin-top: 4px;
        font-size: 10px;
        color: #8a8ea8;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }
      .planet-card-desc {
        margin-top: 6px;
        font-size: 10.5px;
        color: #c4cceb;
        line-height: 1.5;
      }

      /* Aspect list */
      .aspect-list {
        display: flex;
        flex-direction: column;
        gap: 0;
      }
      .aspect-row {
        padding: 8px 0;
        border-top: 1px solid rgba(94,240,216,0.10);
      }
      .aspect-row:first-child { border-top: none; padding-top: 0; }
      .aspect-main {
        font-size: 11.5px;
        color: #eef2ff;
        letter-spacing: 0.04em;
      }
      .aspect-orb {
        font-family: 'DM Mono', monospace;
        color: ${LAVENDER};
        font-size: 10.5px;
      }
      .aspect-tip {
        font-size: 10.5px;
        color: #c3cced;
        margin-top: 3px;
        line-height: 1.5;
      }
      .aspect-tip.mono { font-family: 'DM Mono', monospace; font-size: 10px; color: #8fa3d0; }
      .tip-key { color: ${ACCENT}; letter-spacing: 0.12em; text-transform: uppercase; }

      /* House list */
      .house-list { display: flex; flex-direction: column; }
      .house-row {
        padding: 9px 0;
        border-top: 1px solid rgba(94,240,216,0.10);
      }
      .house-row:first-child { border-top: none; padding-top: 0; }
      .house-main {
        font-size: 11.5px;
        color: #eef2ff;
      }
      .house-num {
        display: inline-block;
        min-width: 22px;
        font-family: 'Playfair Display', 'Georgia', serif;
        color: ${ACCENT};
        font-size: 13px;
      }
      .house-cusp {
        font-family: 'DM Mono', monospace;
        color: ${LAVENDER};
        font-size: 10.5px;
      }
      .house-sub {
        font-size: 10.5px;
        color: #c3cced;
        margin-top: 3px;
        line-height: 1.55;
      }
      .house-sub.mono {
        font-family: 'DM Mono', monospace;
        font-size: 9.5px;
        color: #8fa3d0;
      }
      .dim { color: #8a8ea8; }
      .rx { color: ${LAVENDER}; font-family: 'DM Mono', monospace; font-size: 10px; }

      /* Elemental balance */
      .balance {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 8px;
      }
      .balance-item {
        border: 1px solid rgba(94,240,216,0.18);
        border-radius: 8px;
        padding: 10px;
        background: rgba(255,255,255,0.02);
        text-align: center;
      }
      .balance-label {
        font-size: 9px;
        letter-spacing: 0.3em;
        text-transform: uppercase;
        color: #8a8ea8;
        margin-bottom: 4px;
      }
      .balance-value {
        font-family: 'Playfair Display', 'Georgia', serif;
        font-size: 18px;
        color: ${ACCENT};
      }
      .balance-out { color: #8a8ea8; font-size: 11px; margin-left: 2px; }

      /* Compat signs */
      .compat-signs {
        display: flex;
        gap: 14px;
        flex-wrap: wrap;
      }
      .compat-sign {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
      }
      .compat-sign .sym { font-size: 22px; color: ${LAVENDER}; }
      .compat-sign .nm {
        font-size: 9px;
        letter-spacing: 0.1em;
        color: #8a8ea8;
        text-transform: uppercase;
      }

      /* Daily planetary influences list */
      .planet-list-daily {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .planet-list-daily li {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 11.5px;
        padding-bottom: 8px;
        border-bottom: 1px solid rgba(94,240,216,0.10);
        gap: 12px;
      }
      .planet-list-daily li:last-child { border-bottom: none; padding-bottom: 0; }
      .planet-name {
        color: #8a8ea8;
        font-size: 10.5px;
        letter-spacing: 0.1em;
      }
      .planet-influence {
        color: ${LAVENDER};
        font-size: 11px;
        text-align: right;
      }

      /* Hint */
      .hint {
        margin-top: 8px;
        font-size: 10.5px;
        color: #c7d0f5;
        line-height: 1.5;
      }

      /* Footer */
      .footer {
        padding: 14px 28px 22px;
        border-top: 1px dashed rgba(94,240,216,0.20);
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-family: 'DM Mono', monospace;
        font-size: 10px;
        color: #8a8ea8;
        letter-spacing: 0.12em;
      }

      .page-break-avoid { break-inside: avoid; page-break-inside: avoid; }

      @media print {
        html, body {
          background: #050816 !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .sheet {
          overflow: visible;
          border-radius: 0;
          -webkit-box-decoration-break: slice;
          box-decoration-break: slice;
          background: #050816 !important;
          border: none;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .sheet::before {
          display: none !important;
        }
        .orbit-deco,
        .orbit-deco::before {
          display: none !important;
        }
        .sign-hero,
        .card,
        .mini-stats,
        .planet-card,
        .balance-item {
          background: #13112a !important;
        }
        .card::before {
          display: none !important;
        }
        .sign-symbol-big { filter: none !important; }
        .energy-score { text-shadow: none !important; }
      }
    </style>
    <main class="sheet">
      <header class="header">
        <div class="brand">
          Ecliptica
          <span>${escapeHtml(l.eyebrow)}</span>
        </div>
        <div class="header-date">
          <strong>${escapeHtml(l.exported)}</strong>
          ${escapeHtml(exportedAt)}<br/>
          ${meta}
        </div>
      </header>
      <section class="content">
        ${opt(dailyDivider)}
        ${opt(heroBlock)}
        ${opt(miniStats)}
        ${opt(dailyCard)}
        ${opt(triforceBlock)}
        ${opt(dailySidesRow)}
        ${opt(affirmationCard)}
        ${natalAndBelowBlock}
      </section>
      <footer class="footer">
        <span>${escapeHtml(l.generatedBy)}</span>
        <span>${escapeHtml(exportedAt)}</span>
      </footer>
    </main>
    <script>
      window.__preparePdf = async function () {
        const wheelNodes = Array.from(document.querySelectorAll(".wheel-svg-image[data-wheel-src]"));
        if (!wheelNodes.length) return;
        await Promise.all(wheelNodes.map((node) => new Promise((resolve) => {
          const src = node.getAttribute("data-wheel-src");
          if (!src) return resolve();
          const img = new Image();
          img.decoding = "sync";
          img.onload = () => {
            try {
              const canvas = document.createElement("canvas");
              canvas.width = 880;
              canvas.height = 880;
              const ctx = canvas.getContext("2d");
              if (ctx) {
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                const png = canvas.toDataURL("image/png");
                node.style.backgroundImage = "url('" + png + "')";
              }
            } catch (error) {
              // Keep the SVG background as fallback if rasterization fails.
            }
            node.removeAttribute("data-wheel-src");
            resolve();
          };
          img.onerror = () => {
            node.removeAttribute("data-wheel-src");
            resolve();
          };
          img.src = src;
        })));
      };
    </script>
  `;
}
